import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, ArrowUpRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { GlowButton } from "@/components/GlowButton";
import { NAV_LINKS } from "@/lib/data";
import owlLogo from "@/assets/metis-owl-logo.png";

const NavbarBrand = () => (
  <div className="flex items-center gap-2.5">
    <div className="relative h-[34px] w-[34px]">
      <img
        src={owlLogo}
        alt=""
        aria-hidden="true"
        className="h-full w-full object-contain"
      />
    </div>
    <div className="leading-none">
      <span className="font-display text-[15px] font-semibold tracking-tight text-white">
        Métis
      </span>
      <span className="font-display text-[15px] font-semibold tracking-tight text-neon-g">
        {" "}Tech Labs
      </span>
    </div>
  </div>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const routeFor = (id) => (id === "home" ? "/" : `/${id}`);

  return (
    <header className="fixed inset-x-0 top-0 z-50 flex justify-center px-4">
      <nav
        aria-label="Primary"
        className={`mt-4 flex w-full max-w-6xl items-center justify-between rounded-full glass-strong px-4 transition-[padding,box-shadow,background-color] duration-300 sm:px-5 ${
          scrolled
            ? "py-2 shadow-[0_8px_30px_rgba(0,0,0,0.5),0_0_0_1px_rgba(57,255,20,0.14)]"
            : "py-3"
        }`}
      >
        <Link to="/" aria-label="Métis Tech Labs home" data-testid="navbar-logo">
          <NavbarBrand />
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={routeFor(l.id)}
              end={l.id === "home"}
              data-testid={`navbar-link-${l.id}`}
              className={({ isActive }) =>
                `relative rounded-full px-4 py-2 text-sm transition-colors duration-300 ${
                  isActive
                    ? "text-white"
                    : "text-[color:var(--muted)] hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {l.label}
                  {isActive && (
                    <span className="absolute -bottom-1 left-0 h-px w-full bg-[color:var(--neon-g)] shadow-[0_0_8px_var(--neon-g)]" />
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:block">
          <GlowButton
            as={Link}
            to="/contact"
            pulse
            className="px-5 py-2.5 text-[13px]"
            data-testid="navbar-book-consultation-button"
          >
            Book Consultation <ArrowUpRight size={15} />
          </GlowButton>
        </div>

        {/* Mobile */}
        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                aria-label="Open menu"
                data-testid="navbar-mobile-menu-button"
                className="flex h-10 w-10 items-center justify-center rounded-full glass text-white"
              >
                <Menu size={18} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="border-l border-[rgba(255,255,255,0.08)] bg-[color:var(--bg-1)] text-white">
              <div className="mb-8 mt-2">
                <NavbarBrand />
              </div>
              <div className="flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <SheetClose asChild key={l.id}>
                    <NavLink
                      to={routeFor(l.id)}
                      end={l.id === "home"}
                      data-testid={`navbar-mobile-link-${l.id}`}
                      className={({ isActive }) =>
                        `rounded-xl px-4 py-3 text-base transition-colors ${
                          isActive ? "glass text-neon-g" : "text-[color:var(--muted)]"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </SheetClose>
                ))}
              </div>
              <div className="mt-8">
                <SheetClose asChild>
                  <GlowButton as={Link} to="/contact" pulse className="w-full" data-testid="navbar-mobile-cta-button">
                    Book Consultation <ArrowUpRight size={16} />
                  </GlowButton>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};
